import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Scale, BookOpen, Linkedin, FileText, Clock } from "lucide-react";
import { api } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import NewsletterForm from "../components/NewsletterForm";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/wingman/8b845ce7-5d58-480c-9d7c-7850e94166e2/attachments/1e0b231c6bfb4d719092575079f7837b_image.bin";

const TICKER = [
  "Digital Omnibus — political momentum on Annex III postponement",
  "EU AI Office active on GPAI supervision",
  "Prohibitions (Article 5) — live since 2 Feb 2025",
  "GPAI obligations — live since 2 Aug 2025",
  "Ireland EU Council Presidency — 1 Jul to 31 Dec 2026",
  "Dublin International AI Summit — 14 Oct 2026, RDS",
  "European AI Innovation Month launches October 2026",
];

const PRINCIPLES = [
  { icon: ShieldCheck, title: "Suprarational", text: "Some truths exceed the reach of optimisation. Human dignity, conscience, and moral responsibility cannot be derived from engagement metrics alone." },
  { icon: Scale, title: "Reason Informed by Faith", text: "Disciplined inquiry that does not bracket ultimate questions — and does not pretend they have already been answered." },
  { icon: BookOpen, title: "Divine Revelation", text: "We work within a horizon that recognises sources of meaning older than any technology. Capability does not determine purpose; purpose precedes capability." },
];

export default function Home() {
  const [features, setFeatures] = useState([]);
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [feat, all] = await Promise.all([
          api.get("/articles", { params: { feature: true } }),
          api.get("/articles", { params: { limit: 10 } }),
        ]);
        setFeatures(feat.data);
        setLatest(all.data);
      } catch (e) {
        console.error("Failed to load articles for home page:", e);
      }
    })();
  }, []);

  const featureLead = features[0];
  const featureSecond = features[1];
  const restLatest = latest.filter((a) => a.slug !== featureLead?.slug && a.slug !== featureSecond?.slug).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed cinematic with portrait visible (no crop) */}
      <section
        className="border-b border-black bg-black text-white relative overflow-hidden"
        data-testid="home-hero"
      >
        <div className="w-full px-6 lg:px-12 pt-14 lg:pt-20 pb-16 lg:pb-24">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.28em] text-[var(--eai-accent)]" data-testid="hero-kicker">A briefing for responsible AI</p>
              <h1
                className="mt-6 font-display font-bold tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
                data-testid="hero-headline"
              >
                Curated intelligence for the people building AI we can actually trust.
              </h1>
              <p className="mt-6 font-display italic text-2xl lg:text-3xl text-white tracking-tight" data-testid="hero-subheader">
                Reason Informed by Faith.
              </p>
              <p className="mt-8 text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
                <span className="font-display italic text-white">Ethical AI Now</span> is an independent editorial and research practice — covering the EU AI Act, the Digital Omnibus, Ireland&apos;s 2026 Council Presidency, and the operating procedures responsible AI actually requires.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/manifesto"
                  className="group inline-flex items-center gap-3 border-2 border-[var(--eai-accent)] bg-transparent text-white px-7 py-4 text-base font-medium tracking-wide hover:bg-[var(--eai-accent)] hover:text-white transition-colors duration-200"
                  data-testid="hero-cta-manifesto"
                >
                  Read the Manifesto <ArrowRight size={18} strokeWidth={2} />
                </Link>
                <Link
                  to="/intelligence"
                  className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 text-sm font-medium hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
                  data-testid="hero-cta-intelligence"
                >
                  Read the briefing <ArrowRight size={16} />
                </Link>
                <Link
                  to="/intelligence/ireland-ai-readiness"
                  className="text-sm font-mono-eai uppercase tracking-[0.18em] text-white/70 hover:text-white border-b border-transparent hover:border-white pb-0.5"
                  data-testid="hero-cta-ireland"
                >
                  Ireland&apos;s AI Readiness brief →
                </Link>
              </div>

              <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
                {[
                  ["3", "EU AI Act obligations live"],
                  ["2", "High-risk deadlines under review"],
                  ["6mo", "Irish Presidency window"],
                ].map(([n, l]) => (
                  <div key={l}>
                    <p className="font-display text-4xl lg:text-5xl font-bold leading-none text-white">{n}</p>
                    <p className="mt-2 text-xs font-mono-eai uppercase tracking-[0.18em] text-white/60">{l}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Portrait — full image visible, no crop */}
            <div className="lg:col-span-6 order-1 lg:order-2">
              <figure className="relative w-full">
                <img
                  src="https://customer-assets.emergentagent.com/wingman/8b845ce7-5d58-480c-9d7c-7850e94166e2/attachments/406beb975174470c96a775c3bf850067_image.bin"
                  alt="Matthew Kilkenny — Founder, Ethical AI Now"
                  className="w-full h-auto object-contain max-h-[85vh] mx-auto block"
                  data-testid="home-hero-image"
                />
                <figcaption className="mt-4 flex items-center justify-between text-[11px] font-mono-eai uppercase tracking-[0.22em] text-white/55">
                  <span>Matthew Kilkenny — Founder</span>
                  <span className="flex items-center gap-2"><span className="text-[var(--eai-accent)]">●</span> Edition 01</span>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-t border-white/15 bg-black/40 overflow-hidden">
          <div className="ticker-track py-3 font-mono-eai text-xs uppercase tracking-[0.22em] text-white/70">
            {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
              <span key={`ticker-${i}-${t}`} className="px-8 flex items-center gap-3">
                <span className="text-[var(--eai-accent)]">●</span> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Resource */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-ink)] text-white" data-testid="home-featured-resource">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <FileText size={18} strokeWidth={1.6} className="text-[var(--eai-accent)]" />
              <p className="overline text-[var(--eai-accent)]">Featured resource · Strategic brief</p>
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
              Ireland&rsquo;s AI Readiness:<br />
              <span className="text-white/70">a strategic brief on ethics, innovation &amp; global positioning.</span>
            </h2>
            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-xl">
              Written ahead of Ireland&rsquo;s 2026 EU Council Presidency. A practitioner-and-policy view of what is live, what the Digital Omnibus has delayed, and what the next twelve months will require of regulators and organisations.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/intelligence/ireland-ai-readiness"
                className="inline-flex items-center gap-2 bg-white text-[var(--eai-ink)] px-5 py-3 text-sm font-medium hover:bg-[var(--eai-accent)] hover:text-white transition-colors duration-200"
                data-testid="home-featured-cta"
              >
                Read the brief <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="text-sm font-mono-eai uppercase tracking-[0.18em] text-white/70 hover:text-white border-b border-transparent hover:border-white pb-0.5">
                Request the PDF →
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                ["3 obligations live", "Prohibitions · GPAI · Transparency"],
                ["2 deadlines moving", "Digital Omnibus, trilogue under way"],
                ["1 July 2026", "Irish Presidency begins"],
                ["14 Oct 2026", "Dublin International AI Summit, RDS"],
              ].map(([h, s]) => (
                <div key={h} className="bg-[var(--eai-ink)] p-6">
                  <p className="font-display text-xl lg:text-2xl font-semibold leading-tight">{h}</p>
                  <p className="mt-2 text-xs font-mono-eai uppercase tracking-[0.18em] text-white/50">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence preview — Bento */}
      <section className="border-b border-[var(--eai-border)]" data-testid="home-intelligence-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
            <div className="lg:col-span-4">
              <p className="overline">This week in AI ethics</p>
            </div>
            <div className="lg:col-span-8 flex items-end justify-between gap-6 flex-wrap">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                The intelligence we are reading, writing, and acting on.
              </h2>
              <Link to="/intelligence" className="eai-link reveal-underline text-sm font-mono-eai uppercase tracking-[0.18em]" data-testid="home-view-all-intelligence">
                View all →
              </Link>
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[var(--eai-border)]">
            {featureLead && (
              <div className="lg:col-span-7 bg-white">
                <ArticleCard article={featureLead} size="lg" />
              </div>
            )}
            {featureSecond && (
              <div className="lg:col-span-5 bg-white">
                <ArticleCard article={featureSecond} />
              </div>
            )}
            {restLatest.map((a) => (
              <div key={a.slug} className="lg:col-span-4 bg-white">
                <ArticleCard article={a} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)] eai-grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4">
              <p className="overline">Our editorial frame</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                Three commitments behind everything we publish.
              </h2>
            </div>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--eai-border)]">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="bg-white p-8 lg:p-10 flex flex-col">
                <p.icon size={28} className="text-[var(--eai-ink)]" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-[var(--eai-ink-2)] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHT BANNER — Suprarational Vessel */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-ink)] text-white" data-testid="home-insight-banner">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center">
          <p className="overline text-[var(--eai-accent)]">An editorial axiom</p>
          <blockquote className="mt-10 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-[1.2] tracking-tight">
            “AI solves material problems <span className="text-white/60 not-italic font-mono-eai text-xl lg:text-2xl tracking-[0.05em] align-middle">(Evolutionary Tool)</span> — but the human mind reveals divine truths <span className="text-[var(--eai-accent)] not-italic font-mono-eai text-xl lg:text-2xl tracking-[0.05em] align-middle">(Suprarational Vessel)</span>.”
          </blockquote>
          <div className="mt-10 inline-flex flex-col items-center">
            <div className="w-16 h-px bg-[var(--eai-accent)]" />
            <p className="mt-4 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/60">
              Ethical AI Now · Editorial Frame
            </p>
          </div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="border-b border-[var(--eai-border)]" data-testid="home-founder-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="w-full max-w-md border border-[var(--eai-ink)] bg-[var(--eai-ink)] text-white relative overflow-hidden flex flex-col">
              <div className="aspect-[4/5] relative bg-[var(--eai-ink)]">
                <img
                  src={FOUNDER_IMG}
                  alt="Matthew Kilkenny, Founder of Ethical AI Now"
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid="founder-portrait-home"
                />
                <div className="absolute top-6 left-6 right-6 flex items-start justify-between z-10">
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">Founder</p>
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/70 bg-[var(--eai-ink)]/60 px-2 py-0.5">MK · 2026</p>
                </div>
              </div>
              <div className="border-t border-white/20 p-5">
                <p className="font-display text-xl font-semibold leading-tight">Matthew Kilkenny</p>
                <p className="mt-1 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/70">AI Ethics Ambassador<br/>&amp; Strategic Policy Advisor</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="overline">From the founder</p>
            <blockquote className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
              “The future of technology must be anchored in something older and steadier than technology itself —
              <span className="text-[var(--eai-accent)]"> in human dignity.</span>”
            </blockquote>
            <p className="mt-8 text-lg text-[var(--eai-ink-2)] leading-relaxed max-w-2xl">
              <span className="text-[var(--eai-ink)] font-medium">Matthew Kilkenny</span> is a leading voice in AI ethics, the founder of Ethical AI Now, and a key contributor to Ireland&rsquo;s national readiness for the EU AI Act. With a deep background in the humanities, psychology, and theology, he serves as a strategic thought leader bridging complex AI architecture and the ethical governance needed for a responsible digital future. He authored the strategic brief: <Link to="/intelligence/ireland-ai-readiness" className="text-[var(--eai-ink)] underline decoration-[var(--eai-accent)] decoration-2 underline-offset-4 hover:text-[var(--eai-accent)] transition-colors" data-testid="home-founder-brief-link">&ldquo;Ireland&rsquo;s AI Readiness: A Strategic Brief on Ethics, Innovation &amp; Global Positioning.&rdquo;</Link>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/about" className="eai-btn" data-testid="home-founder-cta">
                Read the founder&rsquo;s brief <ArrowRight size={16} />
              </Link>
              <a
                href="https://www.linkedin.com/in/ethical-ai-now/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border border-[var(--eai-ink)] text-[var(--eai-ink)] hover:bg-[var(--eai-ink)] hover:text-white transition-colors duration-200"
                data-testid="home-founder-linkedin-cta"
              >
                <Linkedin size={16} strokeWidth={1.8} /> Connect on LinkedIn
              </a>
              <Link to="/contact" className="eai-link reveal-underline text-sm font-mono-eai uppercase tracking-[0.18em]" data-testid="home-founder-contact-link">
                Write to Matthew →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* In development strip */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4">
              <p className="overline">In development</p>
            </div>
            <div className="lg:col-span-8 flex items-end justify-between gap-6 flex-wrap">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                Industry guides, tools, and convenings — building now.
              </h2>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--eai-border)]">
            {[
              { to: "/industries", kicker: "Industry guides", title: "Sector-specific operating guidance for healthcare, finance, manufacturing, and the public sector." },
              { to: "/resources", kicker: "Educational resources", title: "Frameworks, templates, and a practitioner toolbox translating AI Act obligations into procedures." },
              { to: "/community", kicker: "Community & events", title: "Roundtables, webinars, and workshops scheduled around Ireland's 2026 Council Presidency." },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="bg-white p-8 lg:p-10 group block" data-testid={`home-coming-soon-${c.to.replace("/", "")}`}>
                <div className="flex items-start justify-between">
                  <p className="overline">{c.kicker}</p>
                  <ArrowUpRight size={20} className="text-[var(--eai-ink-2)] group-hover:text-[var(--eai-accent)] transition-colors" />
                </div>
                <p className="mt-6 font-display text-xl lg:text-2xl font-semibold leading-snug">{c.title}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono-eai uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">
                  <Clock size={13} strokeWidth={1.6} className="text-[var(--eai-accent)]" />
                  Coming soon
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline">The weekly briefing</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              One email. Every Thursday. Nothing you didn&rsquo;t ask for.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </div>
  );
}
